import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function Colleges() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");

  const [form, setForm] = useState({
    id: null,
    name: "",
  });

  const isEditing = form.id !== null;

  // Load list
  const loadData = async (opts = {}) => {
    try {
      setLoading(true);
      const q = opts.search !== undefined ? opts.search : search;
      const { data } = await api.get("/api/admin/colleges", {
        params: { q: q || undefined },
      });
      setItems(data || []);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load colleges");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setForm({ id: null, name: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("College name is required");
      return;
    }

    try {
      setLoading(true);
      const payload = { name: form.name.trim() };

      if (isEditing) {
        await api.put(`/api/admin/colleges/${form.id}`, payload);
      } else {
        await api.post("/api/admin/colleges", payload);
      }

      resetForm();
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to save college");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({ id: item.id, name: item.name || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this college?")) return;
    try {
      setLoading(true);
      await api.delete(`/api/admin/colleges/${id}`);
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete college");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f1f5f9" },
    container: { maxWidth: 950, margin: "0 auto", padding: 16 },
    card: {
      background: "#fff",
      borderRadius: 10,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    label: { fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" },
    btnPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 18px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    btnGhost: {
      background: "#e2e8f0",
      borderRadius: 999,
      padding: "8px 16px",
      border: "none",
      fontSize: 14,
      cursor: "pointer",
    },
  };

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />

      <div style={styles.container}>
        {/* Header row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 14,
          }}
        >
          <h2 style={{ fontSize: 20, margin: 0 }}>College Master</h2>
          <button
            onClick={() => navigate("/dashboard")}
            style={styles.btnGhost}
          >
            ← Back
          </button>
        </div>

        {/* FORM CARD */}
        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            <label style={styles.label}>College Name *</label>
            <input
              style={styles.input}
              value={form.name}
              onChange={(e) =>
                setForm((f) => ({ ...f, name: e.target.value }))
              }
              placeholder="Enter college name"
            />

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} type="submit" disabled={loading}>
                {isEditing ? "Update College" : "Add College"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  style={styles.btnGhost}
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* SEARCH + LIST */}
        <div style={styles.card}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              style={{ ...styles.input, maxWidth: 280 }}
              placeholder="Search college..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              style={styles.btnGhost}
              type="button"
              onClick={() => loadData({ search })}
            >
              Search
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 12 }}>Loading...</div>
          ) : err ? (
            <div style={{ color: "#b91c1c", marginTop: 10 }}>{err}</div>
          ) : !items.length ? (
            <div style={{ padding: 12, color: "#64748b" }}>
              No colleges found
            </div>
          ) : (
            <div style={{ marginTop: 12, overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: 10, textAlign: "left" }}>College Name</th>
                    <th style={{ padding: 10, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      style={{ borderTop: "1px solid #e2e8f0" }}
                    >
                      <td style={{ padding: 10 }}>{item.name}</td>
                      <td
                        style={{
                          padding: 10,
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          style={{
                            ...styles.btnGhost,
                            padding: "4px 12px",
                            marginRight: 6,
                            fontSize: 13,
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          style={{
                            ...styles.btnGhost,
                            padding: "4px 12px",
                            background: "#fee2e2",
                            border: "1px solid #fecaca",
                            fontSize: 13,
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
